import { Link, useParams } from 'react-router-dom';
import { works } from '@/mocks/works';
import Breadcrumb from '@/components/feature/Breadcrumb';
import WorkCard from '@/components/feature/WorkCard';
import useSeo from '@/hooks/useSeo';

export default function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const work = works.find((w) => w.id === id);

  useSeo({
    title: work ? `${work.title} - 吉泽明步影视作品详细介绍` : '作品详情 - 吉泽明步影视资料',
    description: work
      ? `${work.title}（${work.year}，${work.type}）：${work.summary}`
      : '吉泽明步影视作品详细介绍。',
    keywords: work
      ? `吉泽明步,${work.title},${work.type},作品介绍,角色介绍`
      : '吉泽明步,作品介绍',
  });

  if (!work) {
    return (
      <div className="container-page flex flex-col items-center justify-center py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-file-unknow-line text-3xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
          未找到该作品
        </h1>
        <p className="mt-2 text-sm text-foreground-600">该作品可能已下线或链接有误。</p>
        <Link
          to="/works"
          className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-primary-700 cursor-pointer"
        >
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          返回作品列表
        </Link>
      </div>
    );
  }

  const related = works
    .filter((w) => w.id !== work.id && (w.type === work.type || w.tags.some((t) => work.tags.includes(t))))
    .slice(0, 6);

  const facts = [
    { label: '作品类型', value: work.type },
    { label: '上映时间', value: `${work.year} 年` },
    { label: '角色名称', value: work.role },
    { label: '角色定位', value: work.roleType },
    { label: '导演', value: work.director },
    { label: '片长', value: work.runtime },
  ];

  return (
    <div className="w-full">
      <div className="border-b border-background-200/70 bg-background-100/60">
        <div className="container-page py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '作品', to: '/works' },
              { label: work.title },
            ]}
          />
        </div>
      </div>

      <div className="container-page py-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr] lg:gap-12">
          <div className="mx-auto w-full max-w-[360px] lg:mx-0">
            <div className="overflow-hidden rounded-lg border border-background-200/70" style={{ aspectRatio: '2 / 3' }}>
              <img
                src={work.cover}
                alt={`${work.title} 作品封面`}
                title={`${work.title} - 吉泽明步影视作品`}
                className="h-full w-full object-top"
              />
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800">
                {work.type}
              </span>
              {work.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
              {work.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line text-accent-600" aria-hidden="true"></i>
                {work.year} 年
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-500" aria-hidden="true"></i>
                评分 {work.rating.toFixed(1)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-accent-600" aria-hidden="true"></i>
                {work.runtime}
              </span>
            </div>

            <p className="mt-5 text-base leading-relaxed text-foreground-700">{work.summary}</p>

            <dl className="mt-7 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
              {facts.map((fact) => (
                <div key={fact.label} className="flex items-center justify-between border-b border-background-200/70 pb-3">
                  <dt className="text-sm text-foreground-500">{fact.label}</dt>
                  <dd className="text-sm font-medium text-foreground-900">{fact.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/works"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-primary-700 cursor-pointer"
              >
                <i className="ri-apps-2-line" aria-hidden="true"></i>
                浏览同类作品
              </Link>
              <Link
                to="/profile"
                className="inline-flex items-center gap-1.5 rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-800 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-user-star-line" aria-hidden="true"></i>
                查看明星资料
              </Link>
            </div>
          </div>
        </div>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">剧情 / 角色简介</h2>
          <div className="mt-4 max-w-3xl rounded-lg border border-background-200/70 bg-background-100/50 p-6">
            <p className="text-base leading-loose text-foreground-700">{work.story}</p>
          </div>
        </section>

        <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
          <span className="text-foreground-500">所属分类：</span>
          <Link
            to="/works"
            className="rounded-full bg-accent-100 px-3 py-1 text-accent-900 transition-colors hover:bg-accent-200 cursor-pointer"
          >
            {work.type}
          </Link>
          {work.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-secondary-800">
              {tag}
            </span>
          ))}
        </div>

        {related.length > 0 && (
          <section className="mt-12 md:mt-16">
            <div className="mb-6 flex items-end justify-between">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">相关作品推荐</h2>
              <Link to="/works" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap">
                全部作品
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="flex snap-x gap-4 overflow-x-auto pb-4">
              {related.map((item) => (
                <div key={item.id} className="w-[180px] shrink-0 snap-start md:w-[220px]">
                  <WorkCard work={item} />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}